import Link from "next/link"
import { Clock3, Sparkles } from "lucide-react"

import type { Recipe } from "@/types/recipe"
import { cn, formatMinutes } from "@/lib/utils"

type RecipeCardProps = {
  recipe: Recipe
  priority?: boolean
  className?: string
}

const difficultyMap: Record<Recipe["difficulty"], string> = {
  easy: "Легко",
  medium: "Середньо",
  hard: "Складніше",
}

const categoryMap: Record<Recipe["category"], string> = {
  cucumbers: "Огірки",
  tomatoes: "Помідори",
  zucchini: "Кабачки",
  peppers: "Перець",
  eggplants: "Баклажани",
  berries: "Ягоди",
  jam: "Варення",
  compote: "Компоти",
  fermentation: "Ферментація",
  other: "Інше",
}

const seasonMap: Record<string, string> = {
  spring: "Весна",
  summer: "Літо",
  autumn: "Осінь",
  winter: "Зима",
}

function getRecipeEmoji(category: Recipe["category"]) {
  switch (category) {
    case "cucumbers":
      return "🥒"
    case "tomatoes":
      return "🍅"
    case "zucchini":
      return "🥒"
    case "peppers":
      return "🫑"
    case "eggplants":
      return "🍆"
    case "berries":
      return "🫐"
    case "jam":
      return "🍓"
    case "compote":
      return "🍎"
    case "fermentation":
      return "🥬"
    default:
      return "🍯"
  }
}

export function RecipeCard({
  recipe,
  priority = false,
  className,
}: RecipeCardProps) {
  const primarySeason = recipe.season[0]
  const seasonLabel = primarySeason ? seasonMap[primarySeason] : null

  return (
    <article
      className={cn(
        "group overflow-hidden rounded-[1.75rem] border border-neutral-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md",
        className
      )}
    >
      <Link href={`/recipes/${recipe.slug}`} className="block">
        <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-emerald-100 via-amber-50 to-white">
          <div className="absolute inset-0 flex items-center justify-center text-6xl opacity-90 transition-transform duration-300 group-hover:scale-105">
            {getRecipeEmoji(recipe.category)}
          </div>

          <div className="absolute left-4 top-4 inline-flex rounded-full border border-white/80 bg-white/90 px-3 py-1 text-xs font-semibold text-neutral-800 backdrop-blur">
            {categoryMap[recipe.category]}
          </div>

          {priority ? (
            <div className="absolute right-4 top-4 inline-flex items-center rounded-full border border-amber-200 bg-amber-50/95 px-3 py-1 text-xs font-semibold text-amber-800 backdrop-blur">
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              Рекомендуємо
            </div>
          ) : null}
        </div>
      </Link>

      <div className="p-5">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-1 text-xs font-medium text-neutral-700">
            <Clock3 className="mr-1.5 h-3.5 w-3.5" />
            {formatMinutes(recipe.totalTimeMinutes)}
          </span>

          <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800">
            {difficultyMap[recipe.difficulty]}
          </span>

          {seasonLabel ? (
            <span className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-800">
              {seasonLabel}
            </span>
          ) : null}

          {recipe.tags.slice(0, 1).map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800"
            >
              {tag}
            </span>
          ))}
        </div>

        <Link href={`/recipes/${recipe.slug}`} className="block">
          <h3 className="line-clamp-2 text-xl font-semibold leading-snug text-neutral-950 transition-colors group-hover:text-emerald-700">
            {recipe.title}
          </h3>
        </Link>

        <p className="mt-3 line-clamp-3 text-sm leading-7 text-neutral-600">
          {recipe.excerpt}
        </p>

        <div className="mt-5 flex items-center justify-between gap-4">
          <div className="text-xs text-neutral-500">
            {recipe.servings} бан.
          </div>

          <Link
            href={`/recipes/${recipe.slug}`}
            className="inline-flex items-center text-sm font-semibold text-emerald-700 transition-colors hover:text-emerald-800"
          >
            Відкрити рецепт
            <span className="ml-2">→</span>
          </Link>
        </div>
      </div>
    </article>
  )
}